import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import * as rn from 'react-native'

import * as c from '../common'

// import axios from 'axios'
import moment from 'moment'
import { Provider } from 'react-redux'

export const log = (obj: c.ReadonlyRecord<unknown>): void => {
  const deps = c.values(obj)
  // eslint-disable-next-line react-hooks/rules-of-hooks
  React.useEffect(() => {
    console.log(obj)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)
}

export const useIsMounted = () => {
  const isMounted = useRef(false)

  useEffect(() => {
    isMounted.current = true

    return () => {
      isMounted.current = false
    }
  }, [])

  return useCallback(() => isMounted.current, [])
}

export const useBool = (initialState: boolean) => {
  const [currState, setState] = useState<boolean>(initialState)

  const toggle = useCallback((): void => {
    setState((curr) => !curr)
  }, [])

  return [currState, setState, toggle] as const
}

export const useInputVal = (initVal = '') => {
  const [val, setVal] = React.useState<string>(initVal)
  const handleClean = React.useCallback(() => void setVal(''), [])
  const handleInputChange = React.useCallback(
    (e: { target: { value: string } }): void => {
      setVal(e.target.value)
    },
    [],
  )

  return [val, setVal, handleInputChange, handleClean] as const
}

export const useWhatChanged = (deps: unknown[], name: string): void => {
  const refs = deps.map(
    // Hooks can be called inside map() if the array doesn't change length.
    // eslint-disable-next-line react-hooks/rules-of-hooks
    (val): React.MutableRefObject<unknown> => useRef<unknown>(val),
  )

  useEffect((): void => {
    for (let i = 0; i < deps.length; i++) {
      const dep = deps[i]

      if (dep !== refs[i]!.current) {
        c.log(`${name} -> dep changed at index ${i}`)
        refs[i]!.current = dep
      }
    }
    // TODO: Look at why deps are specified this way
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)
}

export const useForceUpdate = () => {
  const [, setTimestamp] = useState(Date.now())

  return useCallback(() => {
    setTimestamp(Date.now())
  }, [])
}

const tbdInstallerOpt: c.Opts = [
  { label: 'To Be Decided', value: 'To Be Decided' },
]
export const useInstallers = (solarCompany: string) => {
  const stateToInstallers = useEnv<c.DBValues>('installers') || c.emptyObj
  const [candidate, setCandidate] = React.useState('To Be Decided')

  const state = c.getCompanyState(solarCompany)

  const opts = React.useMemo((): c.Opts => {
    const dbOpts = Object.keys(
      (stateToInstallers || {})[state || ''] || c.emptyObj,
    ).map((i) => ({
      label: i,
      value: i,
    }))

    if (dbOpts.length === 0) {
      return tbdInstallerOpt
    }

    return tbdInstallerOpt.concat(dbOpts)
  }, [state, stateToInstallers])

  return [opts, candidate, setCandidate] as const
}

export const useBattery = (customerID: string) => {
  const [solarCompany] = useCustomerField(customerID, 'solarCompany')
  const [installers] = useInstallers(solarCompany)
  const [installer] = useCustomerField(
    customerID,
    'main_panel_upgrade_installation_company',
  )
  const batteryTypeStr = useEnv<string>('batteryType') || c.emptyStrArr
  const allBatterySizes =
    useEnv<Record<string, string>>('batterySize') || c.emptyObj
  const [customerBatteryType] = useCustomerField(customerID, 'battery_type')

  const batterySize = React.useMemo(() => {
    if (
      !customerBatteryType ||
      !customerBatteryType ||
      !installer ||
      !allBatterySizes
    ) {
      return c.EMPTY_ARRAY
    }

    const key = `Battery ${customerBatteryType}-${installer}`
    return allBatterySizes[key]
  }, [allBatterySizes, installer, customerBatteryType])

  const batteryTypes = React.useMemo((): c.Opts => {
    const allOptions = c.convertToOptions(batteryTypeStr)
    return allOptions
  }, [batteryTypeStr])

  const batterySizeOpts = React.useMemo(
    (): c.Opts => c.convertToOptions(batterySize),
    [batterySize],
  )

  return [
    installers,
    installer ? batteryTypes : (c.emptyArr as typeof batteryTypes),
    batterySizeOpts,
  ] as const
}

export const initProvider = ({
  fireStorage,
  getUserEmail,
  getUserID,
}: c.InitStoreParams) => {
  const store = c.initStore({
    fireStorage,
    getUserEmail,
    getUserID,
  })

  if (c.IS_WEB) {
    // @ts-ignore
    window.store = store
  }

  return React.memo<{
    children: React.ReactElement
    loading?: React.ReactElement
  }>(({ children }) => <Provider store={store}>{children}</Provider>)
}

interface IChangeEvent {
  readonly target: {
    readonly value: string
  }
}

export const useInput = (initialState: string) => {
  const [currState, setState] = useState<string>(initialState)

  const onChange = useCallback((e: IChangeEvent): void => {
    setState(e.target.value)
  }, [])

  return [currState, setState, onChange] as const
}

export const useTextInput = (initialState: string) => {
  const [currState, setState] = useState<string>(initialState)

  const onChangeText = useCallback((text: string): void => {
    setState(text)
  }, [])

  return [currState, setState, onChangeText] as const
}

export function useCustomer(customerID: string) {
  const selectCustomer = React.useMemo(
    (): ReturnType<typeof c.makeSelectCustomer> => c.makeSelectCustomer(),
    [],
  )

  const selectCustomerArgs = React.useMemo(
    (): c.SelectCustomerParams => ({
      customerID,
    }),
    [customerID],
  )

  const currentCustomer = c.useSelector(
    (_): c.Customer => selectCustomer(_, selectCustomerArgs),
  )

  return [currentCustomer] as const
}

/**
 * Accepts an empty customerID for flexibility of use. If empty, it will not
 * read or write from/to the DB.
 */
export const useCustomerField = <K extends c.CustomerField>(
  customerID: string,
  field: K,
) => {
  type FieldVal = c.Customer[K]
  const [customer] = useCustomer(customerID)
  const remoteValue = customer[field]
  const [localValue, setLocalValue] = React.useState<FieldVal>(remoteValue)

  React.useEffect((): void => {
    setLocalValue(remoteValue)
  }, [remoteValue])

  type SetFieldAction = React.SetStateAction<FieldVal>
  type FieldDispatch = React.Dispatch<SetFieldAction>

  const writeValue = React.useCallback<FieldDispatch>(
    (newValueOrDispatch) => {
      if (typeof newValueOrDispatch === 'function') {
        setLocalValue((prev) => {
          const newVal = newValueOrDispatch(prev)
          setImmediate(() => c.updateCustomer(customerID, { [field]: newVal }))
          return newVal
        })
      } else {
        const newVal = newValueOrDispatch
        setLocalValue(newValueOrDispatch)
        setImmediate(() => c.updateCustomer(customerID, { [field]: newVal }))
      }
    },
    [customerID, field],
  )

  const toggleValue = React.useCallback(() => {
    // @ts-expect-error
    setLocalValue((curr) => {
      const newVal = !curr
      c.updateCustomer(customerID, { [field]: newVal })
      return newVal
    })
  }, [customerID, field])

  return [localValue as c.Customer[K], writeValue, toggleValue] as const
}

export const useSessionVal = <Cast extends c.DBVal>(key: string) => {
  const selectSessionVal = React.useMemo(() => c.makeSelectSessionVal(), [])
  const selectSessionValArgs = React.useMemo(
    (): c.SelectSessionValParams => ({
      key,
    }),
    [key],
  )
  const currVal = c.useSelector(
    (_): c.DBVal => selectSessionVal(_, selectSessionValArgs),
  ) as Cast

  type WriteVal = Dispatch<SetStateAction<Cast>>
  const writeVal = React.useCallback<WriteVal>(
    (valueOrSetter) => {
      c.dispatch(
        c.setSessionVal({
          key,
          value:
            typeof valueOrSetter === 'function'
              ? valueOrSetter(currVal)
              : valueOrSetter,
        }),
      )
    },
    [currVal, key],
  )

  const toggleVal = React.useCallback(() => {
    c.dispatch(
      c.setSessionVal({
        key,
        value: !currVal,
      }),
    )
  }, [currVal, key])

  return [currVal, writeVal, toggleVal] as const
}

export const useCustomerFieldInput = (
  customerID: string,
  field: c.CustomerField,
) => {
  const [customerData] = useCustomer(customerID)

  const [localVal, setLocalVal] = React.useState('')

  const { current: inputEvBatcher } = React.useRef(
    c.createEventBatcher<string>({
      debounceTime: 100,
    }),
  )
  React.useEffect(() => {
    inputEvBatcher.onEvents((updates): void => {
      if (updates.length > 0) {
        c.updateCustomer(customerID, {
          [field]: updates[updates.length - 1],
        })
      }
    })
    return (): void => {
      inputEvBatcher.flush()
      inputEvBatcher.off()
    }
  }, [customerID, field, inputEvBatcher])

  const write = React.useCallback(
    (newVal: string) => {
      setLocalVal(newVal)
      inputEvBatcher.eventReceived(newVal)
    },
    [inputEvBatcher],
  )

  const handle = React.useCallback(
    (e: { target: { value: string } }): void => {
      write(e.target.value)
    },
    [write],
  )

  React.useEffect(() => {
    setLocalVal(customerData[field] as string)
  }, [customerData, field])

  return [localVal, write, handle] as const
}

export const useEnv = <Cast extends c.DBVal>(key: string): Cast | undefined => {
  // In the future, env will be separate from settings
  const selectEnv = React.useMemo(() => c.makeSelectEnv(), [])
  const selectEnvArgs = React.useMemo(
    (): c.SelectEnvParams => ({
      key,
    }),
    [key],
  )
  const currVal = c.useSelector(
    (_): c.DBVal => selectEnv(_, selectEnvArgs),
  ) as Cast

  return currVal
}

export const ghostDiv: HTMLDivElement = (() => {
  if (c.IS_WEB) {
    // @ts-ignore TODO
    return document.createElement('div')
  }
  return null as unknown as HTMLDivElement
})()
export const ghostInput: HTMLInputElement = (() => {
  if (c.IS_WEB) {
    // @ts-ignore TODO
    return document.createElement('input')
  }
  return null as unknown as HTMLInputElement
})()
export const ghostP = (() => {
  if (c.IS_WEB) {
    // @ts-ignore TODO
    return document.createElement('p')
  }
  return null as unknown as HTMLParagraphElement
})()

//#region Rowable

export interface RowableUsed<T extends c.Rowable = c.Rowable> {
  addRow(): void
  changeVal(
    col: string,
    rowID: string,
    val: React.SetStateAction<number | string>,
  ): void
  cols: c.Cols
  deleteRow(rowID: string): void
  rows: readonly T[]
}

export const useRowable = <T extends c.Rowable = c.Rowable>(
  customerID: string,
  field: c.FieldRowable,
): RowableUsed<T> => {
  const [createdAt] = useCustomerField(customerID, 'createdAt')
  const [homeRep] = useCustomerField(customerID, 'homeRep')
  const [company] = useCustomerField(customerID, 'solarCompany')
  const state = c.getCompanyState(company) as
    | 'arizona'
    | 'california'
    | 'nevada'

  //#region global
  const windowColorsStr = useEnv<string>('windowColors') || ''
  const windowColors = JSON.parse(windowColorsStr || '[]') as readonly string[]

  const secCamPrices = c.useSelector(c.selectEfficiencyPrice('security_cams'))
  const secCamItems = useEnv<c.Opts>('security_cams') || c.emptyOpts

  const secSysPrices = c.useSelector(c.selectEfficiencyPrice('security_system'))
  const secSysItems = useEnv<c.Opts>('security_system') || c.emptyOpts

  const secTVPrices = c.useSelector(c.selectEfficiencyPrice('security_tv'))
  const secTVItems = useEnv<c.Opts>('security_tv') || c.emptyOpts

  const secWifiCamPrices = c.useSelector(
    c.selectEfficiencyPrice('security_wifi_cameras'),
  )
  const secWifiCamItems = useEnv<c.Opts>('security_wifi_cameras') || c.emptyOpts

  const ceilingFanTypes = useEnv<c.strings>('ceilingFanTypes')
  //#endregion

  const cols = React.useMemo((): c.Cols => {
    const _cols = c.rowableToCols[field]

    if (field === 'ceiling_fan_detail') {
      const theCols = _cols.slice()
      ;(theCols[0] as c.Writable<c.OptCol>).opts =
        ceilingFanTypes?.map((value) => ({
          label: value,
          value,
        })) || c.EMPTY_ARRAY

      return theCols
    }
    if (field === 'security_cams_detail') {
      const theCols = _cols.slice()

      ;(theCols[0] as c.Writable<c.OptCol>).opts = secCamItems

      return theCols
    }
    if (field === 'security_system_detail') {
      const theCols = _cols.slice()

      ;(theCols[0] as c.Writable<c.OptCol>).opts = secSysItems

      return theCols
    }
    if (field === 'security_wifi_cameras_detail') {
      const theCols = _cols.slice()

      ;(theCols[0] as c.Writable<c.OptCol>).opts = secWifiCamItems

      return theCols
    }
    if (field === 'security_tv_detail') {
      const theCols = _cols.slice()

      ;(theCols[0] as c.Writable<c.OptCol>).opts = secTVItems

      return theCols
    }
    if (field === 'new_windows_sqft_each_window') {
      const shouldShowGlobalColor = createdAt > c.Jun4_2024

      const theCols = _cols.slice()

      if (windowColors)
        (theCols[2] as c.OptCol) = {
          ...(theCols[2] as c.OptCol),
          opts: windowColors.map(
            (c): c.Opt => ({
              label: c,
              value: c,
            }),
          ),
        }

      const isGiovanniCustomer = homeRep.toLowerCase().includes('giovanni')
      // always an old customer
      if (isGiovanniCustomer) {
        theCols.push(c.windowTypeCol)
      }

      if (shouldShowGlobalColor) {
        theCols.splice(2, 1)
        theCols[0] = {
          ...theCols[0],
          widthPer: 30,
        } as c.Col
        theCols[1] = {
          ...theCols[1],
          widthPer: 30,
        } as c.Col
        theCols[2] = {
          ...theCols[2],
          widthPer: 30,
        } as c.Col
      }

      // Remove individual color selector for newer customers
      return theCols
    }

    return _cols
  }, [
    ceilingFanTypes,
    createdAt,
    field,
    homeRep,
    secCamItems,
    secSysItems,
    secTVItems,
    secWifiCamItems,
    windowColors,
  ])

  const [rowsStr, setRowsStr] = useCustomerField(customerID, field)

  const addRow = React.useCallback<RowableUsed['addRow']>((): void => {
    setRowsStr((currRowsStr) => {
      const newRows = JSON.parse(currRowsStr || '[]') as c.Rowable[]

      if (field === 'ceiling_fan_detail') {
        ;(newRows as c.CeilingFan[]).push(c.newCeilingFan())
      }
      if (field === 'security_cams_detail') {
        ;(newRows as c.CamGroup[]).push(c.newCamGroup())
      }
      if (field === 'security_wifi_cameras_detail') {
        ;(newRows as c.CamGroup[]).push(c.newCamGroup())
      }
      if (field === 'security_system_detail') {
        ;(newRows as c.SecSysGroup[]).push(c.newSecSysGroup())
      }
      if (field === 'security_tv_detail') {
        ;(newRows as c.SecurityTV[]).push(c.newSecurityTV())
      }
      if (field === 'mini_split_tons') {
        ;(newRows as c.MiniSplit[]).push(c.newMiniSplit())
      }
      if (field === 'new_windows_sliding_glass_sqft') {
        ;(newRows as c.SlidingGlassDoorGroup[]).push(
          c.newSlidingGlassDoorGroup(),
        )
      }
      if (field === 'new_windows_sqft_each_window') {
        ;(newRows as c.WindowGroup[]).push(c.newWindowGroup())
      }
      if (field === 'outlet_detail') {
        ;(newRows as c.OutletGroup[]).push(c.newOutletGroup())
      }

      return JSON.stringify(newRows)
    })
  }, [field, setRowsStr])

  const changeVal = React.useCallback<RowableUsed['changeVal']>(
    (colKey, rowID, val): void => {
      if (field === 'security_cams_detail' && !secCamPrices) {
        console.debug('Security cam prices not ready, aborting')
        return
      }
      if (field === 'security_wifi_cameras_detail' && !secWifiCamPrices) {
        console.debug('Security wifi cam prices not ready, aborting')
        return
      }
      if (field === 'security_system_detail' && !secSysPrices) {
        console.debug('Security system prices not ready, aborting')
        return
      }
      if (field === 'security_tv_detail' && !secTVPrices) {
        console.debug('Security tv prices not ready, aborting')
        return
      }
      setRowsStr((currRowsStr) => {
        const newRows = JSON.parse(
          currRowsStr || '[]',
        ) as c.Writable<c.Rowable>[]

        const theRow = newRows.find((r) => r.id === rowID)

        if (!theRow) {
          // The row was deleted
          return currRowsStr
        }

        const colType = cols.find((_) => _.key === colKey)!.type

        const newVal = (() => {
          if (typeof val === 'function') {
            return val(theRow[colKey as keyof typeof theRow])
          } else {
            return val
          }
        })()
        const newValSanitized =
          colType === 'input'
            ? (newVal as string).replace('-', '').replace('+', '')
            : // Why was this needed?
              // .replace(',', '')
              newVal

        theRow[colKey as keyof typeof theRow] = newValSanitized as any

        if (field === 'security_cams_detail' && (theRow as c.CamGroup).type) {
          const prices =
            state === 'california'
              ? secCamPrices!.california.pricesByCity[0]!.list
              : secCamPrices![state].list
          const price = prices.find(
            (p) => p.label === (theRow as c.CamGroup).type,
          )!.value

          ;(theRow as c.Writable<c.CamGroup>).price = price
        }

        if (
          field === 'security_wifi_cameras_detail' &&
          (theRow as c.CamGroup).type
        ) {
          const prices =
            state === 'california'
              ? secWifiCamPrices!.california.pricesByCity[0]!.list
              : secWifiCamPrices![state].list
          const price = prices.find(
            (p) => p.label === (theRow as c.CamGroup).type,
          )!.value

          ;(theRow as c.Writable<c.CamGroup>).price = price
        }

        if (
          field === 'security_system_detail' &&
          !!(theRow as c.SecSysGroup).type
        ) {
          const prices =
            state === 'california'
              ? secSysPrices!.california.pricesByCity[0]!.list
              : secSysPrices![state].list

          const price = prices.find(
            (p) => p.label === (theRow as c.SecSysGroup).type,
          )!.value

          ;(theRow as c.Writable<c.SecSysGroup>).price = price
        }

        if (colKey === 'qty') {
          const multiInputKey = cols.find((c) => c.type === 'multi-input')?.key
          if (multiInputKey) {
            const currVals = JSON.parse(
              theRow[multiInputKey as keyof typeof theRow],
            )

            const qty = c.parseInt((theRow as any).qty)
            if (currVals.length !== qty) {
              const newArr = c.resizeArray(currVals, qty, '')
              theRow[multiInputKey as keyof typeof theRow] =
                JSON.stringify(newArr)
            }
          }
        }

        if (field === 'security_tv_detail' && !!(theRow as c.SecurityTV).type) {
          const prices =
            state === 'california'
              ? secTVPrices!.california.pricesByCity[0]!.list
              : secTVPrices![state].list

          const price = prices.find((p) =>
            p.label.startsWith((theRow as c.SecurityTV).type),
          )!.value

          ;(theRow as c.Writable<c.SecurityTV>).price = price
        }

        if (colKey === 'qty') {
          const multiInputKey = cols.find((c) => c.type === 'multi-input')?.key
          if (multiInputKey) {
            const currVals = JSON.parse(
              theRow[multiInputKey as keyof typeof theRow],
            )

            const qty = c.parseInt((theRow as any).qty)
            if (currVals.length !== qty) {
              const newArr = c.resizeArray(currVals, qty, '')
              theRow[multiInputKey as keyof typeof theRow] =
                JSON.stringify(newArr)
            }
          }
        }

        return JSON.stringify(newRows)
      })
    },
    [
      cols,
      field,
      secCamPrices,
      secSysPrices,
      secTVPrices,
      secWifiCamPrices,
      setRowsStr,
      state,
    ],
  )

  const deleteRow = React.useCallback<RowableUsed['deleteRow']>(
    (rowID) => {
      setRowsStr((currRowsStr) => {
        const newRows = JSON.parse(currRowsStr) as c.Rowable[]

        const rowIdx = newRows.findIndex((r) => r.id === rowID)

        if (rowIdx === 0 && newRows.length === 1) {
          if (field === 'mini_split_tons') {
            return JSON.stringify([c.newMiniSplit()])
          }
          if (field === 'new_windows_sliding_glass_sqft') {
            return JSON.stringify([c.newSlidingGlassDoorGroup()])
          }
          if (field === 'new_windows_sqft_each_window') {
            return JSON.stringify([c.newWindowGroup()])
          }
        }

        newRows.splice(rowIdx, 1)
        return JSON.stringify(newRows)
      })
    },
    [field, setRowsStr],
  )

  const rows = JSON.parse(rowsStr || '[]') as T[]

  if (!c.rowableFields.includes(field)) {
    throw new Error(`Wrong field: ${field}`)
  }

  return {
    addRow,
    changeVal,
    cols,
    deleteRow,
    rows,
  }
}
//#endregion

export const useWindowColors = (): c.Opts => {
  const windowColorsStr = useEnv<string>('windowColors') || ''
  const windowColors = JSON.parse(windowColorsStr || '[]') as c.strings
  const windowColorOpts = windowColors.map(
    (c): c.Opt => ({
      label: c,
      value: c,
    }),
  )
  return windowColorOpts
}

export const useInsulation = (customerID: string): c.Opts => {
  const [createdAt] = useCustomerField(customerID, 'createdAt')
  const [solarCompany] = useCustomerField(customerID, 'solarCompany')
  const [currInsulationType] = useCustomerField(
    customerID,
    'attic_insulation_type',
  )

  const insulationTypes = React.useMemo((): c.Opts => {
    if (typeof createdAt !== 'number') {
      console.warn(
        `useInsulation() -> ${customerID} customer's createdAt not a number: ${typeof createdAt}`,
      )
      return c.emptyArr as c.Opts
    }
    if (!solarCompany) {
      return c.emptyArr as c.Opts
    }

    // White labels won't have such old customers
    const isOldCustomer = moment(createdAt).isBefore(
      c.NEW_INSULATION_TYPES_CUTOFF,
    )
    const insulationTypesToUse = isOldCustomer
      ? c.insulationTypesOld[solarCompany]
      : c.companyToInsulationTypes[solarCompany]

    if (!insulationTypesToUse) {
      return [
        {
          label: currInsulationType,
          value: currInsulationType,
        },
      ]
    }

    return insulationTypesToUse.map((value) => ({
      label: value,
      value,
    }))
  }, [createdAt, currInsulationType, customerID, solarCompany])

  return insulationTypes
}

export const useTotalCost = (
  type: 'company' | 'customer',
  customerID: string,
  deductDisabled?: boolean,
): number => {
  const twoStoryFee = useEnv<number>('twoStoryFee') || 0

  // c.debug('useTotalCost()')
  const customer = c.useSelector((s) => c.selectCustomer(s, customerID))
  if (!customer) {
    // c.debug('useTotalCost() -> returning 0 as no customer found')
    return 0
  }

  return c.calcTotalCost({
    customer,
    deductDisabled,
    // Must be mirrored in usePayment()
    twoStoryFee,
    type,
  })
}

export const MUIBreakpoints = {
  xs: 0,
  sm: 600,
  md: 900,
  lg: 1200,
  xl: 1536,
  laptop: 1090,
} as const

//#region theming
export const useTheme = (colorScheme?: 'dark' | 'light') => {
  const _colorSchemeRN = rn.useColorScheme()
  const sysColorScheme =
    _colorSchemeRN === 'unspecified' ? 'light' : _colorSchemeRN

  return c.themeTuple[colorScheme || sysColorScheme || 'light']
}
//#endregion theming
//#region address
// const placesBaseURL = 'https://maps.googleapis.com/maps/api/place'

type Geometry = c.r<{ location: { lat: number; lng: number } }>

export type Prediction = c.r<{
  description: string
  /* Not actually contained in the autocomplete endpoint, but in the details one */
  geometry: Geometry
  place_id: string
}>

// let webAutocomplete: google.maps.places.AutocompleteService =
//   null as unknown as google.maps.places.AutocompleteService

export const setGoogService = (
  //@ts-ignore
  s: google.maps.places.AutocompleteService,
): void => {
  // @ts-ignore
  if (Math.random() === s) {
  } // avoid unused var
  // webAutocomplete = s
}

// const getPredictionsForAddress = async (q: string): Promise<c.Opts> => {
//   return Promise.resolve([])
//   // if (c.IS_WEB)
//   //   return new Promise((res, rej) => {
//   //     webAutocomplete.getQueryPredictions(
//   //       {
//   //         input: q,
//   //       },
//   //       (data, status) => {
//   //         if (status === 'OK') {
//   //           res(
//   //             data?.map((p) => ({
//   //               label: p.description,
//   //               value: p.place_id!,
//   //             })) || [],
//   //           )
//   //         } else {
//   //           rej(new Error(status))
//   //         }
//   //       },
//   //     )
//   //   })

//   // const placesURL = `${placesBaseURL as string}/autocomplete/json?key=${c
//   //   .env['REACT_APP_G_API_KEY']!}&language=en&components=country:us&input=`
//   // type PlacesRes = c.r<{
//   //   predictions: Prediction[]
//   // }>
//   // const res = await axios.request({
//   //   method: 'get',
//   //   url: `${placesURL}${q}`,
//   // })
//   // if (res.status === 200)
//   //   return (res.data as PlacesRes).predictions.map(
//   //     (p): c.Opt => ({ label: p.description, value: p.place_id }),
//   //   )
//   // else throw new Error(`${getPredictionsForAddress.name} -> res.status != 200`)
// }

// const getPredictionDetails = async (
//   placeID: string,
// ): Promise<Prediction | null> => {
//   return Promise.resolve(null)
//   if (c.IS_WEB) return new Promise((res, rej) => {
//     res()
//   })

//   const placesDetailsURL = `${placesBaseURL}/details/json?key=${c.processEnv[
//     'REACT_APP_G_API_KEY'
//   ]!}&place_id=`
//   type PlaceRes = c.r<{ result: Prediction }>
//   const res = await axios.request({
//     method: 'get',
//     url: placesDetailsURL + placeID,
//   })
//   if (res.status === 200) return (res.data as PlaceRes).result
//   else throw new Error(`${getPredictionDetails.name} -> res.status != 200`)
// }

export const usePredictions = (
  customerID: string,
  q: string,
  gApiKey: string,
) => {
  if (Math.random() > 1) console.log(customerID, q, gApiKey) // avoid unused var

  return [[] as c.Opts, c.emptyFn] as const
  // const [predictions, setPredictions] = React.useState<c.Opts>([])

  // React.useEffect((): void => {
  //   if (q === '') return void setPredictions(c.EMPTY_ARRAY)
  //   const thisQ = q

  //   thisQ &&
  //     getPredictionsForAddress(thisQ).then((p) => {
  //       if (q === thisQ) setPredictions(p)
  //     })
  // }, [gApiKey, q])

  // const handlePredictionSelect = React.useCallback(
  //   async (placeID: string): Promise<void> => {
  //     const place = predictions.find((p) => p.value === placeID)!
  //     c.updateCustomer(customerID, {
  //       addressLat: 0,
  //       addressLng: 0,
  //       customerAddress: place.label,
  //     })
  //   },
  //   [customerID, predictions],
  // )

  // const [currAddress] = useCustomerField(customerID, 'customerAddress')
  // const [currLat] = useCustomerField(customerID, 'addressLat')
  // const [currLng] = useCustomerField(customerID, 'addressLng')

  // /* Fetch the coordinates for an existing address if the address is actually
  //  recognized by Google */
  // React.useEffect(() => {
  //   ;(async () => {
  //     // const [prediction = { label: '', value: '' }] = predictions
  //     // if (prediction.label === currAddress && currLat === 0 && currLng === 0) {
  //     //   c.debug(`Fetching coordinates for address ${currAddress}`, {
  //     //     predictions,
  //     //     currLat,
  //     //     currLng,
  //     //   })
  //     //   const fullPlace = await getPredictionDetails(prediction.value)
  //     //   if (fullPlace?.geometry) {
  //     //     c.updateCustomer(customerID, {
  //     //       addressLat: fullPlace.geometry.location.lat,
  //     //       addressLng: fullPlace.geometry.location.lng,
  //     //     })
  //     //   } else {
  //     //     c.debug(`No geometry for ${currAddress}`)
  //     //   }
  //     // }
  //   })()
  // }, [currAddress, currLat, currLng, customerID, predictions])

  // return [predictions, handlePredictionSelect] as const
}

export const openMapsAt = async (lat: number, lng: number): Promise<void> => {
  const browserURL = `https://www.google.de/maps/@${lat},${lng}`
  const url: string =
    rn.Platform.select({
      android: `geo://?q=${lat},${lng}`,
      ios: `maps://?q=${lat},${lng}`,
    }) || browserURL

  rn.Linking.openURL(url)
}
//#endregion address

export const empty = <rn.View />

export const isMobile = rn.Platform.OS === 'android' || rn.Platform.OS === 'ios'
export const isWeb = rn.Platform.OS === 'web'

export const isSafari = () => {
  if (isMobile) return false
  // @ts-ignore
  if (typeof navigator !== 'object' || navigator === null) return false
  // @ts-ignore
  return /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
}

export const RowWrapper = isMobile ? rn.View : React.Fragment
// export const RowWrapper = React.Fragment;

/**
 * Returns a list of tags for the efficiencies turned on.
 */
export const useServicesRendered = (customerID: string): string => {
  const [customer] = useCustomer(customerID)

  const servicesRendered = React.useMemo(
    (): string[] =>
      c.nonCustomEfficiencyKeys
        .filter((k) => customer[k] === 'yes')
        .map((k) => c.getFieldLabel(k)),
    [customer],
  )

  const customFieldsUsed = c.customFields.filter((f) => customer[f] === 'yes')

  if (customFieldsUsed.length) {
    const customFieldsNamed = customFieldsUsed
      .map((f) => customer[(f + '_name') as c.CustomerField])
      .filter(c.identity) // reject empty names
      .join('/')

    return servicesRendered.concat(customFieldsNamed).join('/')
  }

  return servicesRendered.join('/') || '[No Services Rendered]'
}

export const useCRMUrl = (customerID: string): null | string => {
  const [crmID] = useCustomerField(customerID, 'crm_id')
  const [solarCompany] = useCustomerField(customerID, 'solarCompany')
  const companyToLocationID = useEnv('companyToCRMLocationID')

  if (!crmID) return null
  if (!c.isObj(companyToLocationID)) return null

  const locationID = companyToLocationID[solarCompany]

  if (!locationID) return null

  return `https://app.magnetixagency.com/v2/location/${locationID}/contacts/detail/${crmID}`
}

/**
 * If a product was enabled in the past but is now no longer available, it will
 * be returned in the list of available products.
 */
export const useProducts = (customerID: string) => {
  const productToLabel = useEnv<c.Dict<string>>('productToLabel') || c.emptyObj

  const stateToProductsRaw =
    useEnv<c.Dict<string>>('stateToProducts') || c.emptyObj
  const stateToProducts = c.mapValues(
    stateToProductsRaw,
    // @ts-ignore
    (_) => JSON.parse(_) as c.strings,
  )
  // useCustomerField() update bug
  const [{ products, solarCompany }] = useCustomer(customerID)
  const currState = c.getCompanyState(solarCompany)
  const [, writeProducts] = useCustomerField(customerID, 'products')

  const availProductsForThisState = stateToProducts[currState] || c.EMPTY_ARRAY

  const currProducts: c.strings =
    products === '' ? c.EMPTY_ARRAY : products.split(' / ')

  const toggleHIL = React.useCallback((): void => {
    if (currProducts.includes('HIL')) {
      writeProducts(currProducts.filter((p) => p !== 'HIL').join(' / '))
    } else {
      writeProducts(currProducts.concat('HIL').sort().join(' / '))
    }
  }, [currProducts, writeProducts])
  const toggleRC = React.useCallback((): void => {
    if (currProducts.includes('RC')) {
      writeProducts(currProducts.filter((p) => p !== 'RC').join(' / '))
    } else {
      writeProducts(currProducts.concat('RC').sort().join(' / '))
    }
  }, [currProducts, writeProducts])
  const toggleSecurity = React.useCallback((): void => {
    if (currProducts.includes('Security')) {
      writeProducts(currProducts.filter((p) => p !== 'Security').join(' / '))
    } else {
      writeProducts(currProducts.concat('Security').sort().join(' / '))
    }
  }, [currProducts, writeProducts])
  const toggleSolar = React.useCallback((): void => {
    if (currProducts.includes('Solar')) {
      writeProducts(currProducts.filter((p) => p !== 'Solar').join(' / '))
    } else {
      writeProducts(currProducts.concat('Solar').sort().join(' / '))
    }
  }, [currProducts, writeProducts])

  const availProductsFinal = React.useMemo((): c.Opts => {
    if (customerID) {
      return [...availProductsForThisState, ...currProducts]
        .sort()
        .reverse()
        .filter(c.uniq)
        .map((value) => ({
          label: productToLabel[value] || value,
          value,
        }))
    }

    return Object.keys(productToLabel).map((value) => ({
      label: productToLabel[value] || value,
      value,
    }))
  }, [availProductsForThisState, currProducts, customerID, productToLabel])

  return {
    availProducts: availProductsFinal,
    currProducts,
    toggleHIL,
    toggleRC,
    toggleSecurity,
    toggleSolar,
  }
}

export type Focusable = c.r<{
  blur(): void
  focus(): void
}>

const unselectedContractCompany: c.ContractCompany = Object.freeze({
  address1: '[[Select a company for the contract]]',
  address2: '[[Select a company for the contract]]',
  logo: '',
  longName: '[[Select a company for the contract]]',
  phoneNumber: '[[Select a company for the contract]]',
  roofingLicense: '[[Select a company for the contract]]',
  shortName: '',
  states: '',
})
const jan282025 = c.normalizeTimestampToMs(1738072142000)
export const useContractCompany = (customerID: string) => {
  const contractCompaniesAllStates =
    useEnv<c.ContractCompanies>('contractCompanies') || c.emptyObj

  const [{ contractCompany, createdAt, solarCompany: oldSolarCompanyVal }] =
    useCustomer(customerID)

  const state = c.getCompanyState(oldSolarCompanyVal)
  const contractCompaniesCurrState = React.useMemo(
    (): c.ContractCompanies =>
      c.pickBy(contractCompaniesAllStates, ({ states }) =>
        states.includes(state || ''),
      ),
    [contractCompaniesAllStates, state],
  )

  const contractCompanyFull: c.ContractCompany =
    contractCompaniesAllStates[contractCompany] || unselectedContractCompany

  const handleContractCompany = React.useCallback(
    (e: { target: { value: string } }): void => {
      c.updateCustomer(customerID, {
        contractCompany: e.target.value,
      })
    },
    [customerID],
  )

  const shouldOfferCompanySelector = createdAt > jan282025

  const contractCompaniesCurrStateArr = React.useMemo(
    () => c.values(contractCompaniesCurrState),
    [contractCompaniesCurrState],
  )

  React.useEffect(() => {
    // If there's just one company, auto-selected it
    if (
      shouldOfferCompanySelector &&
      contractCompaniesCurrStateArr.length === 1
    ) {
      const [theCompany] = contractCompaniesCurrStateArr
      handleContractCompany({ target: { value: theCompany!.shortName } })
    }
  }, [
    contractCompaniesCurrStateArr,
    handleContractCompany,
    shouldOfferCompanySelector,
  ])

  return {
    contractCompaniesAllStates,
    contractCompaniesCurrState,
    contractCompaniesCurrStateArr,
    contractCompanyFull,
    contractCompanyShortName: contractCompany,
    handleContractCompany,
    shouldOfferCompanySelector,
  } as const
}

const feb10_2025 = 1739211101441
export const useSolarSurveyMedia = (customerID: string) => {
  const [createdAt] = useCustomerField(customerID, 'createdAt')
  const solarSurveyMediaKinds = useEnv<c.MediaKinds>('solarSurveyMediaKinds')

  if (createdAt > feb10_2025) {
    if (solarSurveyMediaKinds) {
      return [solarSurveyMediaKinds, c.mediaToLabelSolarSurveyNew] as const
    }
    return [c.solarSurveyMediaKindsNew, c.mediaToLabelSolarSurveyNew] as const
  }
  return [c.solarSurveyMediaKindsOld, c.mediaToLabelSolarSurveyOld] as const
}

export type Bullets = c.r<
  Array<{
    /**
     * Must be from the material community icon set inside
     * react-native-vector-icons
     */
    icon: string
    text: string
  }>
>

type Term = c.r<{
  /**
   * Dealer fee, in floating number format, 0.1 is 10%, 0.01 is 1%, and so on.
   */
  cost: number
  desc: string
  interest: number
  loanCode?: string
  maxAmt?: number
  /**
   * Minimum amount in dollars required for the loan.
   */
  minAmt?: number
  years: number
}>

export type LoanCompanies = c.r<{
  [K: string]: {
    states: string
    contractorID?: string
    programPhoneNumber?: string
    offers: {
      [x: string]: {
        bullets: Bullets
        choicesInMonths?: [firstBullet: string, secondBullet: string]
        desc: string
        /**
         * Must be from the material community icon set inside
         * react-native-vector-icons
         */
        icon: string
        plans?: {
          [x: string]: {
            /**
             * Not the actual length itself necessarily, but whatever applies
             * for any special promotions, as specified inside in the offer via
             * the optional choicesInMonths.
             */
            months?: number
            terms: {
              [x: string]: Term
            }
          }
        }
        shortDesc: string
        terms?: {
          [x: string]: Term
        }
      }
    }
  }
}>

export const useLoanCompanies = (customerID: string): LoanCompanies => {
  const loanCompaniesUnparsed = useEnv('loanCompanies')
  const [state] = useCustomerField(customerID, 'solarCompany')
  // Firebase doesn't support dots or slashes in object keys
  const loanCompanies = JSON.parse(
    JSON.stringify(
      loanCompaniesUnparsed || {
        'Loading...': {
          offers: {},
          states: '',
        },
      },
    )
      .replaceAll('(((dot)))', '.')
      .replaceAll('(((slash)))', '/'),
  )

  return c.pickBy(loanCompanies as LoanCompanies, (lc) =>
    lc.states.includes(state),
  )
}

export const PAYMENT_METHOD_OPTS: c.Opts<c.PaymentMethod> = [
  {
    label: 'Financing',
    value: 'financing',
  },
  {
    label: 'Cash',
    value: 'cash',
  },
] as const

export const usePayment = (customerID: string) => {
  /**
   * Nirvana only
   */
  const twoStoryFee = useEnv<number>('twoStoryFee') || 0

  const CASH_DEALER_FEE = useEnv<number>('cashDealerFee') || 0
  const CASH_TERM = React.useMemo(
    (): Term => ({
      cost: CASH_DEALER_FEE,
      desc: 'Cash',
      interest: 0,
      years: 0,
    }),
    [CASH_DEALER_FEE],
  )

  const [paymentVal, paymentSet] = useCustomerField(
    customerID,
    'security_loan_applied',
  )
  const paymentData = React.useMemo((): c.PayMethodApplied | null => {
    if (typeof paymentVal === 'string' && paymentVal) {
      return JSON.parse(paymentVal) as c.PayMethodApplied
    }
    return null
  }, [paymentVal])
  const paymentDataRef = React.useRef(paymentData)
  paymentDataRef.current = paymentData
  const paymentHandle = React.useCallback(
    (partial: Partial<c.PayMethodApplied>): void => {
      paymentSet(
        JSON.stringify({ ...(paymentDataRef.current || {}), ...partial }),
      )
    },
    [paymentSet],
  )

  const [paymentIsApplied, paymentIsAppliedSet] = useCustomerField(
    customerID,
    'payment_is_applied',
  )

  const paymentMethodVal: c.PaymentMethod =
    (paymentData?.paymentMethod as c.PaymentMethod) ?? 'financing'
  const paymentMethodSet = React.useCallback(
    (val: c.PaymentMethod): void => {
      paymentHandle({ paymentMethod: val })
    },
    [paymentHandle],
  )

  const availLoanCompanies = useLoanCompanies(customerID!)
  const availLoanCompaniesNames = React.useMemo(
    () => Object.keys(availLoanCompanies),
    [availLoanCompanies],
  )
  const availLoanCompaniesOpts = React.useMemo(
    (): c.Opts =>
      Object.keys(availLoanCompanies).map((value) => ({ label: value, value })),
    [availLoanCompanies],
  )
  const loanCompanyName = paymentData?.loanCompanyName ?? ''
  const loanCompany = availLoanCompanies[loanCompanyName]
  const loanCompanyNameHandle = React.useCallback(
    (eOrVal: c.InputEvent | string): void => {
      const newVal = typeof eOrVal === 'string' ? eOrVal : eOrVal.target.value
      paymentHandle({
        loanCompanyName: newVal,
        offerName: '',
        planName: '',
        termInterest: 0,
        termName: '',
        termYears: 0,
      })
    },
    [paymentHandle],
  )

  const availOffers = React.useMemo(
    // Reverse sort makes it so "Standard" is on top, at least based on current
    // offers
    () =>
      Object.entries(loanCompany?.offers || c.emptyObj).sort(([a], [b]) =>
        b.localeCompare(a),
      ),
    [loanCompany?.offers],
  )
  const availOffersOpts = React.useMemo(
    (): c.Opts =>
      availOffers.map(([value]) => ({
        label: value,
        value,
      })),
    [availOffers],
  )
  const offerName = paymentData?.offerName ?? ''
  const offer = loanCompany?.offers[offerName]
  const offerNameHandle = React.useCallback(
    (eOrVal: c.InputEvent | string): void => {
      const newVal = typeof eOrVal === 'string' ? eOrVal : eOrVal.target.value
      paymentHandle({
        offerName: newVal,
        planName: '',
        termInterest: 0,
        termName: '',
        termYears: 0,
      })
    },
    [paymentHandle],
  )

  const availPlans = React.useMemo(
    () =>
      c
        .entries(offer?.plans || c.emptyObj)
        .sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true })),
    [offer],
  )
  const availPlansOpts = React.useMemo(
    (): c.Opts =>
      availPlans.map(([value]) => ({
        label: value,
        value,
      })),
    [availPlans],
  )
  const planName = paymentData?.planName ?? ''
  const plan = offer?.plans?.[planName]
  const planNameHandle = React.useCallback(
    (newPlanName: string): void => {
      paymentHandle({
        planName: newPlanName,
        termInterest: 0,
        termName: '',
        termYears: 0,
      })
    },
    [paymentHandle],
  )

  const availTerms = React.useMemo(
    () =>
      c
        .entries(plan?.terms || offer?.terms || c.emptyObj)
        .sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true })),
    [offer, plan],
  )
  const availTermsOpts = React.useMemo(
    (): c.Opts => availTerms.map(([value]) => ({ label: value, value })),
    [availTerms],
  )
  const termName = paymentData?.termName ?? ''
  const term =
    paymentMethodVal === 'cash'
      ? CASH_TERM
      : plan?.terms?.[termName] || offer?.terms?.[termName]
  const termNameHandle = React.useCallback(
    (val: string): void => {
      const _term =
        paymentMethodVal === 'cash'
          ? CASH_TERM
          : plan?.terms?.[val] || offer?.terms?.[val]
      console.debug({ plan, offer })
      paymentHandle({
        termInterest: _term!.interest,
        termName: val,
        termYears: _term!.years,
      })
    },
    [CASH_TERM, offer, paymentHandle, paymentMethodVal, plan],
  )

  //#region applied
  const userID = c.getUserID()
  const admins = useEnv<c.Dict<boolean>>('admins')
  const isAdmin = !!admins?.[userID ?? '']
  const canOverrideUsers = useEnv<c.Dict<boolean>>('canOverride')
  const canOverride = isAdmin || !!canOverrideUsers?.[userID ?? '']
  const [customer] = useCustomer(customerID)
  const { security_alarm_payout, security_contract_sign_customer } = customer

  const { rows: _secWifiCamRows, changeVal: secWifiCamRowsChange } = (() => {
    if (c.CustomerSchema['security_wifi_cameras_detail']) {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      return useRowable<c.CamGroup>(customerID, 'security_wifi_cameras_detail')
    }
    return {
      rows: c.emptyArr as c.CamGroup[],
      changeVal: c.emptyFn as RowableUsed['changeVal'],
    }
  })()
  const secWifiCamRows = _secWifiCamRows.filter((cg) => !!cg.qty && !!cg.type)
  const { rows: _secCamRows, changeVal: secCamRowsChange } = (() => {
    if (c.CustomerSchema['security_cams_detail']) {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      return useRowable<c.CamGroup>(customerID, 'security_cams_detail')
    }
    return {
      rows: c.emptyArr as c.CamGroup[],
      changeVal: c.emptyFn as RowableUsed['changeVal'],
    }
  })()
  const secCamRows = _secCamRows.filter((cg) => !!cg.qty && !!cg.type)
  const { rows: _secSysRows, changeVal: secSysRowsChange } = (() => {
    if (c.CustomerSchema['security_system_detail']) {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      return useRowable<c.SecSysGroup>(customerID, 'security_system_detail')
    }
    return {
      rows: c.emptyArr as c.SecSysGroup[],
      changeVal: c.emptyFn as RowableUsed['changeVal'],
    }
  })()
  const secSysRows = _secSysRows.filter((ssg) => !!ssg.qty && !!ssg.type)
  const { rows: _secTVRows, changeVal: secTVRowsChange } = (() => {
    if (c.CustomerSchema['security_tv_detail']) {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      return useRowable<c.SecurityTV>(customerID, 'security_tv_detail')
    }
    return {
      rows: c.emptyArr as c.SecurityTV[],
      changeVal: c.emptyFn as RowableUsed['changeVal'],
    }
  })()
  const secTVRows = _secTVRows.filter((ssg) => !!ssg.type)

  const totalCostOverall = useTotalCost('company', customerID, false)

  const STATE_TO_LEAD_FEE =
    useEnv<{ [x: string]: number }>('stateToLeadFee') || c.emptyObj
  /**
   * Total cost + fees:
   *   - Lead fee: UPS's fee
   *   - Alarm payout
   *   - Two story home fee (Nirvana)
   *
   * This amount won't be shown on UI, only `totalCostOverall` will
   */
  const totalCostPlusFees =
    totalCostOverall +
    c.parseFloating(security_alarm_payout) +
    STATE_TO_LEAD_FEE[customer.solarCompany] +
    // Must be mirrored in useTotalCost()
    (c.CODENAME === 'nirvana' &&
    c.CustomerSchema.homeStoryNumber &&
    customer.homeStoryNumber === '2'
      ? twoStoryFee
      : 0)

  const MAX_LOAN_COMMISSION = useEnv<number>('maxLoanCommission') || 0

  /**
   * The loan amount without any commission
   */
  const customGrandTotalWdfLowerBound =
    totalCostPlusFees / (1 - (term?.cost || 0))
  const customGrandTotalWdfIs =
    paymentData != null &&
    (paymentData.grandTotalWDF % 50 !== 0 ||
      paymentData.finalCommission < 0 ||
      paymentData.finalCommission > MAX_LOAN_COMMISSION)
  const customGrandTotalWdfVal = customGrandTotalWdfIs
    ? paymentData!.grandTotalWDF
    : -1
  const customGrandTotalWdfSet = React.useCallback(
    (val: number): void => {
      if (!term) {
        paymentHandle({ grandTotalWDF: val })
        return
      }
      const multiplier = 1 / (1 - (term?.cost || 0))
      const newSubtotal = val / multiplier
      const newFinalCommission = newSubtotal - totalCostPlusFees
      paymentHandle({
        finalCommission: newFinalCommission,
        grandTotalWDF: val,
        subtotal: newSubtotal,
      })
    },
    [paymentHandle, term, totalCostPlusFees],
  )

  const customCommissionVal = paymentData?.finalCommission ?? 0

  const customCommissionHandle = React.useCallback(
    (newCommission: number) => {
      if (paymentIsApplied || !term) return

      const multiplier = 1 / (1 - (term?.cost || 0))

      const newSubtotal = totalCostPlusFees + newCommission
      const newGrandTotalWDF = newSubtotal * multiplier

      const roundedGrandTotal =
        Math.round((newGrandTotalWDF + Number.EPSILON) / 50) * 50

      const adjustmentTotal = roundedGrandTotal - newGrandTotalWDF

      // convert total adjustment back into commission adjustment
      const adjustment = adjustmentTotal / multiplier

      const newCommissionAdjusted = newCommission + adjustment
      const newSubtotalAdjusted = totalCostPlusFees + newCommissionAdjusted

      paymentHandle({
        finalCommission: newCommissionAdjusted,
        grandTotalWDF: roundedGrandTotal,
        subtotal: newSubtotalAdjusted,
      })
    },
    [paymentHandle, paymentIsApplied, term, totalCostPlusFees],
  )

  const finalCommission = customGrandTotalWdfIs
    ? (() => {
        if (!term) return 0
        const multiplier = 1 / (1 - (term?.cost || 0))
        const subtotal = customGrandTotalWdfVal / multiplier
        return subtotal - totalCostPlusFees
      })()
    : c.parseFloating(customCommissionVal)

  const subtotal = totalCostPlusFees + finalCommission
  const grandTotalWDF = customGrandTotalWdfIs
    ? customGrandTotalWdfVal
    : subtotal / (1 - (term?.cost || 0))

  const appliedLoan = paymentIsApplied ? paymentData : null

  const addersWithCustomerCost = React.useMemo(
    (): c.CustomerFields => c.getAddersWithCustomerCost(customer),
    [customer],
  )

  /**
   * Update customer cost and individual prices to reflect commission
   */
  const appliedLoanApply = React.useCallback((): void => {
    paymentHandle({
      finalCommission,
      grandTotalWDF,
      subtotal,
      totalCost: totalCostOverall,
    })
    paymentIsAppliedSet(true)

    c.updateCustomer(customerID, {
      has_touched_customer_cost: Object.fromEntries(
        addersWithCustomerCost.map((eff) => [eff, true]),
      ),
    })

    const percents = addersWithCustomerCost.map((eff) => {
      const cost = c.parseFloating(customer[(eff + '_cost') as c.CustomerField])
      return cost / totalCostOverall
    })
    const grandTotals = percents.map((perc) => grandTotalWDF * perc)

    const newData = {
      ...Object.fromEntries(
        addersWithCustomerCost.map((eff, i) => [
          (eff + '_customer_cost') as c.CustomerField,
          grandTotals[i]!.toFixed(4),
        ]),
      ),
    }

    if (
      c.CustomerSchema['security_cams_detail'] &&
      customer.security_cams === 'yes'
    ) {
      const totalCostCams = secCamRows.reduce(
        (acc, row) => acc + row.price * row.qty,
        0,
      )
      const percentCams = totalCostCams / totalCostOverall
      const grandTotalCams = grandTotalWDF * percentCams
      newData['security_cams_detail'] = c.applyLoanToSecurity(
        secCamRows,
        grandTotalCams,
      )
      console.debug('totalCostCams', totalCostCams)
      console.debug('secCamRows', secCamRows)
      console.debug('newData', JSON.parse(newData['security_cams_detail']))
    }
    if (
      c.CustomerSchema['security_wifi_cameras_detail'] &&
      customer.security_wifi_cameras === 'yes'
    ) {
      const totalCostWifiCams = secWifiCamRows.reduce(
        (acc, row) => acc + row.price * row.qty,
        0,
      )
      const percentWifiCams = totalCostWifiCams / totalCostOverall
      const grandTotalWifiCams = grandTotalWDF * percentWifiCams
      newData['security_wifi_cameras_detail'] = c.applyLoanToSecurity(
        secWifiCamRows,
        grandTotalWifiCams,
      )
    }
    if (
      c.CustomerSchema['security_system_detail'] &&
      customer.security_system === 'yes'
    ) {
      const totalCostSys = secSysRows.reduce(
        (acc, row) => acc + row.price * row.qty,
        0,
      )
      const percentSys = totalCostSys / totalCostOverall
      const grandTotalSys = grandTotalWDF * percentSys
      newData['security_system_detail'] = c.applyLoanToSecurity(
        secSysRows,
        grandTotalSys,
      )
    }
    if (
      c.CustomerSchema['security_tv_detail'] &&
      customer.security_tv === 'yes'
    ) {
      const totalCostTV = secTVRows.reduce((acc, row) => acc + row.price, 0)
      const percentTV = totalCostTV / totalCostOverall
      const grandTotalTV = grandTotalWDF * percentTV
      newData['security_tv_detail'] = c.applyLoanToSecurity(
        secTVRows,
        grandTotalTV,
      )
    }

    setTimeout(() => {
      c.updateCustomer(customerID, newData)
    }, 1000) // TODO
  }, [
    customer,
    customerID,
    addersWithCustomerCost,
    finalCommission,
    grandTotalWDF,
    paymentHandle,
    paymentIsAppliedSet,
    secCamRows,
    secSysRows,
    secTVRows,
    secWifiCamRows,
    subtotal,
    totalCostOverall,
  ])

  const appliedLoanUnlock = React.useCallback(() => {
    if (!!security_contract_sign_customer && !canOverride) {
      return
    }
    if (
      c.CustomerSchema['security_cams_detail'] &&
      customer.security_cams === 'yes'
    ) {
      for (const camRow of secCamRows) {
        // Trigger a change so the original price gets put back in
        secCamRowsChange('qty', camRow.id, camRow.qty)
      }
    }
    if (
      c.CustomerSchema['security_wifi_cameras_detail'] &&
      customer.security_wifi_cameras === 'yes'
    ) {
      for (const camRow of secWifiCamRows) {
        // Trigger a change so the original price gets put back in
        secWifiCamRowsChange('qty', camRow.id, camRow.qty)
      }
    }
    if (
      c.CustomerSchema['security_system_detail'] &&
      customer.security_system === 'yes'
    ) {
      for (const sysRow of secSysRows) {
        // Trigger a change so the original price gets put back in
        secSysRowsChange('qty', sysRow.id, sysRow.qty)
      }
    }
    if (
      c.CustomerSchema['security_tv_detail'] &&
      customer.security_tv === 'yes'
    ) {
      for (const tvRow of secTVRows) {
        // Trigger a change so the original price gets put back in
        secTVRowsChange('type', tvRow.id, tvRow.type)
      }
    }

    // Revert customer costs to just the cost
    c.updateCustomer(customerID, {
      has_touched_customer_cost: Object.fromEntries(
        addersWithCustomerCost.map((eff) => [eff, false]),
      ),
    })
    paymentIsAppliedSet(false)
  }, [
    canOverride,
    customer.security_cams,
    customer.security_system,
    customer.security_tv,
    customer.security_wifi_cameras,
    customerID,
    addersWithCustomerCost,
    secCamRows,
    secCamRowsChange,
    secSysRows,
    secSysRowsChange,
    secTVRows,
    secTVRowsChange,
    secWifiCamRows,
    secWifiCamRowsChange,
    paymentIsAppliedSet,
    security_contract_sign_customer,
  ])
  //#endregion

  return {
    appliedLoan,
    appliedLoanApply,
    appliedLoanCustomGrandTotalWdfLowerBound: customGrandTotalWdfLowerBound,
    appliedLoanFinalCommission: finalCommission,
    appliedLoanFinalCommissionHandle: customCommissionHandle,
    appliedLoanGrandTotalWDF: paymentIsApplied
      ? paymentData?.grandTotalWDF ?? grandTotalWDF
      : grandTotalWDF,
    appliedLoanGrandTotalWDFHandle: customGrandTotalWdfSet,
    appliedLoanNoDataInputted: totalCostOverall === 0,
    appliedLoanTotalCostOverall: totalCostOverall,
    appliedLoanUnlock,

    availLoanCompanies,
    availLoanCompaniesNames,
    availLoanCompaniesOpts,
    loanCompany,
    loanCompanyName,
    loanCompanyNameHandle,

    availOffers,
    availOffersOpts,
    offer,
    offerName,
    offerNameHandle,

    availPlans,
    availPlansOpts,
    plan,
    planName,
    planNameHandle,

    availTerms,
    availTermsOpts,
    term,
    termName,
    termNameHandle,

    paymentMethodSet,
    paymentMethodVal,
  } as const
}

export const useCategoryToMediaKinds = (customerID: string) => {
  const { currProducts } = useProducts(customerID)
  const asEntries = c.entries(c.categoryToMediaKinds)
  return asEntries.filter(([category]) => {
    if (category === 'Alarms') {
      return (
        currProducts.includes('Security') ||
        (currProducts.length === 0 && c.CODENAME !== 'nirvana')
      )
    }
    return true
  })
}

//#region security
const securityCamsMediaKindsIs4: c.MediaKind[] = [
  'security_cam0',
  'security_cam1',
  'security_cam2',
  'security_cam3',
]
const securityCamsMediaKindsIs6: c.MediaKind[] = [
  ...securityCamsMediaKindsIs4,
  'security_cam4',
  'security_cam5',
]
export const useSecurityCamAngles = (
  customerID: string,
): readonly c.MediaKind[] => {
  // TODO: Why isn't useCustomerField here working correctly?
  const [{ security_cams_is_6: camsIs6 }] = useCustomer(customerID)

  if (camsIs6 === 'yes') return securityCamsMediaKindsIs6
  return securityCamsMediaKindsIs4
}
//#endregion security

export type DetailLine = c.r<{
  id: string
  name: string
  notes: string[]
  price: number
  qty: number
}>

const dashes = '-----------------------------------------------'

export const useDetailLines = (customerID: string) => {
  //#region global
  const [customer] = useCustomer(customerID)
  const {
    security_cams,
    security_cams_show,
    security_system,
    security_system_show,
    security_tv,
    security_tv_show,
    security_wifi_cameras,
    security_wifi_cameras_show,
  } = customer
  const { rows: _secCamRows } = useRowable<c.CamGroup>(
    customerID,
    'security_cams_detail',
  )
  const { rows: _secSysRows } = useRowable<c.SecSysGroup>(
    customerID,
    'security_system_detail',
  )
  const { rows: _secTVRows } = useRowable<c.SecurityTV>(
    customerID,
    'security_tv_detail',
  )
  const { rows: _secWifiCamRows } = useRowable<c.CamGroup>(
    customerID,
    'security_wifi_cameras_detail',
  )
  //#endregion global
  //#region local
  const [collapsed, collapsedSet] = React.useState<c.DictS<boolean>>(c.emptyObj)
  const collapsedHandleMax = React.useMemo<c.DictS<() => void>>(
    () =>
      c.mapValues(c.adderToInputs, (_, efficiencyKey) => () => {
        collapsedSet((_) => ({ ..._, [efficiencyKey]: false }))
      }),
    [],
  )
  const collapsedHandleMin = React.useMemo<c.DictS<() => void>>(
    () =>
      c.mapValues(c.adderToInputs, (_, efficiencyKey) => () => {
        collapsedSet((_) => ({ ..._, [efficiencyKey]: true }))
      }),
    [],
  )

  //#endregion local

  const nonSecurityActiveEfficiencies = React.useMemo(
    () =>
      c.efficiencyKeys
        .filter((k) => !k.startsWith('security_'))
        .filter((k) => !k.startsWith('custom_'))
        .filter((k) => customer[k] === 'yes')
        .map((field): DetailLine => {
          const notes = (
            customer[(field + '_notes') as c.CustomerField] as string
          ).trim()

          const extraNotes =
            (
              customer[(field + 'extra_notes') as c.CustomerField] as string
            )?.trim() || ''

          const notesLines = notes.length === 0 ? [] : notes.split('\n')
          const extraNotesLines =
            extraNotes.length === 0 ? [] : extraNotes.split('\n')

          const qty = customer[(field + '_qty') as c.CustomerField] as number

          return {
            id: field,
            name: c.getFieldLabel(field),
            notes: [...notesLines, ...extraNotesLines],
            price: c.parseFloating(
              customer[(field + '_customer_cost') as c.CustomerField],
            ),
            qty: qty || -1,
          }
        }),
    [customer],
  )

  const secCamRows =
    security_cams === 'no' || security_cams_show === 0
      ? (c.EMPTY_ARRAY as c.CamGroup[])
      : _secCamRows.filter((cg) => !!cg.qty && !!cg.type)

  const secSysRows =
    security_system === 'no' || security_system_show === 0
      ? (c.EMPTY_ARRAY as c.SecSysGroup[])
      : _secSysRows.filter((ssg) => !!ssg.qty && !!ssg.type)

  const secTVRows =
    security_tv === 'no' || security_tv_show === 0
      ? (c.EMPTY_ARRAY as c.SecurityTV[])
      : _secTVRows.filter((ssg) => !!ssg.type)

  const secWifiCamRows =
    security_wifi_cameras === 'no' || security_wifi_cameras_show === 0
      ? (c.EMPTY_ARRAY as c.CamGroup[])
      : _secWifiCamRows.filter((cg) => !!cg.qty && !!cg.type)

  const customItems = c
    .getFilledCustomFields(customer)
    .map((customField): DetailLine => {
      const qty = customer[(customField + '_qty') as c.CustomerField] as number

      const customerCost = customer[
        (customField + '_customer_cost') as c.CustomerField
      ] as string

      const notes = (
        customer[(customField + '_notes') as c.CustomerField] as string
      ).trim()

      return {
        id: customField,
        name: customer[(customField + '_name') as c.CustomerField] as string,
        notes: notes.length === 0 ? [] : notes.split('\n'),
        price: c.parseFloating(customerCost) / qty,
        qty: qty,
      }
    })

  const detailLines = React.useMemo(
    (): readonly DetailLine[] => [
      ...customItems,
      ...nonSecurityActiveEfficiencies,
      //#region securityCamsItems
      ...(customer.security_cams === 'no' || secCamRows.length === 0
        ? []
        : [
            {
              id: 'security_cams',
              name: collapsed['security_cams']
                ? c.getFieldLabel('security_cams')
                : '--------------- Security Camera ---------------',
              notes: [],
              price: collapsed['security_cams']
                ? c.parseFloating(customer.security_cams_customer_cost)
                : -1,
              qty: -1,
            },
          ]),
      ...(customer.security_cams === 'no' || collapsed['security_cams']
        ? []
        : secCamRows
      ).map(
        ({ id, notes, price, qty, type }): DetailLine => ({
          id,
          name: type,
          notes: [notes],
          price,
          qty,
        }),
      ),
      ...(customer.security_cams === 'no' ||
      secCamRows.length === 0 ||
      customer.security_cams_extra_notes.trim().length === 0 ||
      collapsed['security_cams']
        ? []
        : [
            {
              id: 'security_cams_extra_notes',
              name: dashes,
              notes: customer.security_cams_extra_notes.trim().split('\n'),
              price: -1,
              qty: -1,
            },
            {
              id: '/security_cams_extra_notes',
              name: dashes,
              notes: [],
              price: -1,
              qty: -1,
            },
          ]),
      //#endregion securityCamsItems
      //#region securitySystemItems
      ...(customer.security_system === 'no' || secSysRows.length === 0
        ? []
        : [
            {
              id: 'security_system',
              name: collapsed['security_system']
                ? c.getFieldLabel('security_system')
                : '--------------- Security System ---------------',
              notes: [],
              price: collapsed['security_system']
                ? c.parseFloating(customer.security_system_customer_cost)
                : -1,
              qty: -1,
            },
          ]),
      ...(customer.security_system === 'no' || collapsed['security_system']
        ? []
        : secSysRows
      ).map(
        ({ id, notes, price, qty, type }): DetailLine => ({
          id,
          name: type,
          notes: [notes],
          price,
          qty,
        }),
      ),
      ...(customer.security_system === 'no' ||
      secSysRows.length === 0 ||
      customer.security_system_extra_notes.trim().length === 0 ||
      collapsed['security_system']
        ? []
        : [
            {
              id: 'security_system_extra_notes',
              name: dashes,
              notes: customer.security_system_extra_notes.trim().split('\n'),
              price: -1,
              qty: -1,
            },
            {
              id: '/security_system_extra_notes',
              name: dashes,
              notes: [],
              price: -1,
              qty: -1,
            },
          ]),
      //#endregion securitySystemItems
      //#region securityTvItems
      ...(customer.security_tv === 'no' || secTVRows.length === 0
        ? []
        : [
            {
              id: 'security_tv',
              name: collapsed['security_tv']
                ? c.getFieldLabel('security_tv')
                : '--------------------- TVs ---------------------',
              notes: [],
              price: collapsed['security_tv']
                ? c.parseFloating(customer.security_tv_customer_cost)
                : -1,
              qty: -1,
            },
          ]),
      ...(customer.security_tv === 'no' || collapsed['security_tv']
        ? []
        : secTVRows
      ).map(
        ({ id, price, qty, type }): DetailLine => ({
          id,
          name: type,
          notes: [],
          price,
          qty,
        }),
      ),
      ...(customer.security_tv === 'no' ||
      secTVRows.length === 0 ||
      customer.security_tv_extra_notes.trim().length === 0 ||
      collapsed['security_tv']
        ? []
        : [
            {
              id: 'security_tv_extra_notes',
              name: dashes,
              notes: customer.security_tv_extra_notes.trim().split('\n'),
              price: -1,
              qty: -1,
            },
            {
              id: '/security_tv_extra_notes',
              name: dashes,
              notes: [],
              price: -1,
              qty: -1,
            },
          ]),
      //#endregion securityTvItems
      //#region securityWifiCamerasItems
      ...(customer.security_wifi_cameras === 'no' || secWifiCamRows.length === 0
        ? []
        : [
            {
              id: 'security_wifi_cameras',
              name: collapsed['security_wifi_cameras']
                ? c.getFieldLabel('security_wifi_cameras')
                : '--------------- WiFi Camera ---------------',
              notes: [],
              price: collapsed['security_wifi_cameras']
                ? c.parseFloating(customer.security_wifi_cameras_customer_cost)
                : -1,
              qty: -1,
            },
          ]),
      ...(customer.security_wifi_cameras === 'no' ||
      collapsed['security_wifi_cameras']
        ? []
        : secWifiCamRows
      ).map(
        ({ id, notes, price, qty, type }): DetailLine => ({
          id,
          name: type,
          notes: [notes],
          price,
          qty,
        }),
      ),
      ...(customer.security_wifi_cameras === 'no' ||
      secWifiCamRows.length === 0 ||
      customer.security_wifi_cameras_extra_notes.trim().length === 0 ||
      collapsed['security_wifi_cameras']
        ? []
        : [
            {
              id: 'security_wifi_cameras_extra_notes',
              name: dashes,
              notes: customer.security_wifi_cameras_extra_notes
                .trim()
                .split('\n'),
              price: -1,
              qty: -1,
            },
            {
              id: '/security_wifi_cameras_extra_notes',
              name: dashes,
              notes: [],
              price: -1,
              qty: -1,
            },
          ]),
      //#endregion securityWifiCamerasItems
    ],
    [
      collapsed,
      customItems,
      customer.security_cams,
      customer.security_cams_customer_cost,
      customer.security_cams_extra_notes,
      customer.security_system,
      customer.security_system_customer_cost,
      customer.security_system_extra_notes,
      customer.security_tv,
      customer.security_tv_customer_cost,
      customer.security_tv_extra_notes,
      customer.security_wifi_cameras,
      customer.security_wifi_cameras_customer_cost,
      customer.security_wifi_cameras_extra_notes,
      nonSecurityActiveEfficiencies,
      secCamRows,
      secSysRows,
      secTVRows,
      secWifiCamRows,
    ],
  )

  return {
    collapsed,
    collapsedHandleMax,
    collapsedHandleMin,
    detailLines,
  }
}
